import type { ToolkitOrder } from '@aloft/toolkit-shared';
import { Typography, useModal } from '@aloft/toolkit-sui';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { useGetTkv2OrderAPI } from '../../hooks/api/schema-hooks/useGetTkv2OrderAPI.ts';

const ORDER_STATUS_POLL_INTERVAL_MS = 1000 * 30;

type GuardTarget = {
  modalBody: string;
  title: string;
  primaryActionText: string;
  secondaryActionText?: string;
  to: '/orders' | '/completed-orders';
};

const getGuardTarget = (order: ToolkitOrder): GuardTarget | undefined => {
  if (order.isDeleted) {
    return {
      title: 'Order unavailable',
      modalBody:
        'This order was unassigned from your account. If you believe this was a mistake, please contact our team using the button below.',
      primaryActionText: 'Return to Orders',
      secondaryActionText: 'Contact Support',
      to: '/orders',
    };
  }

  // Internal valuation appraisals (self-managed by the client) stay editable even after the
  // report has been generated/downloaded, so these orders should never be guarded away from the
  // edit screen just because they're "completed". Mock orders keep today's lock-and-redirect
  // behavior even if `isInternalValuation` is also set.
  if (order.isCompleted && (!order.isInternalValuation || order.isMock)) {
    return {
      title: 'Order completed',
      modalBody: 'This order was already completed. You can review it in your completed orders.',
      primaryActionText: 'View completed orders',
      to: '/completed-orders',
    };
  }
  return undefined;
};

export const useOrderStatusGuard = (orderID: string) => {
  const navigate = useNavigate();
  const [guardTarget, setGuardTarget] = useState<GuardTarget | undefined>();
  const hasRedirected = useRef(false);
  const { Modal, openModal, isModalOpen } = useModal();

  const order = useGetTkv2OrderAPI(
    { pathParams: { orderID } },
    {
      meta: { showGlobalLoader: true },
      refetchInterval: ORDER_STATUS_POLL_INTERVAL_MS,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: true,
    },
  );

  useEffect(() => {
    if (!order.data || hasRedirected.current) return;

    const nextGuardTarget = getGuardTarget(order.data);
    if (!nextGuardTarget) return;

    hasRedirected.current = true;
    setGuardTarget(nextGuardTarget);
    openModal();
  }, [openModal, order.data]);

  const orderStatusModal = useMemo(
    () =>
      guardTarget && isModalOpen ? (
        <Modal
          isOpen={isModalOpen}
          onClose={() => {}}
          title={guardTarget.title}
          showCloseButton={false}
          closeOnEsc={false}
          primaryAction={{
            text: guardTarget.primaryActionText,
            onClick: () => navigate(guardTarget.to, { replace: true }),
          }}
          secondaryAction={
            guardTarget.secondaryActionText
              ? {
                  text: guardTarget.secondaryActionText,
                  onClick: () => {
                    window.open('mailto:orders@appraisify.com', '_blank');
                  },
                }
              : undefined
          }
        >
          <Typography variant="body1">{guardTarget.modalBody}</Typography>
        </Modal>
      ) : null,
    [Modal, guardTarget, isModalOpen, navigate],
  );

  return { order, orderStatusModal };
};
